import { MAX_PROFILE_ID_LENGTH, MAX_VISITED_PROFILE_IDS, gatewayErrorEnvelopeSchema, normalizeSpacePasswordText, wakeLoopRequestContextSchema } from "@wakeloop/schemas";
import { toErrorMessage as toErrorMessage$1 } from "@wakeloop/utils";

//#region src/audit.ts
function contextAuditFields(context) {
	return {
		requestId: context.actorContext.requestId,
		wakeLoopProfileId: context.actorContext.wakeLoopProfileId,
		profileKind: context.actorContext.profileKind
	};
}

//#endregion
//#region src/headers.ts
const WAKELOOP_HEADER_REQUEST_ID = "x-wakeloop-request-id";
const WAKELOOP_HEADER_CALLER_PRINCIPAL_ID = "x-wakeloop-caller-principal-id";
const WAKELOOP_HEADER_PROFILE_ID = "x-wakeloop-profile-id";
const WAKELOOP_HEADER_PROFILE_KIND = "x-wakeloop-profile-kind";
const WAKELOOP_HEADER_PROFILE_HANDLE = "x-wakeloop-profile-handle";
const WAKELOOP_HEADER_PROFILE_NAME = "x-wakeloop-profile-name";
const WAKELOOP_HEADER_OWNER_USER_ID = "x-wakeloop-owner-user-id";
const WAKELOOP_HEADER_OWNER_NAME = "x-wakeloop-owner-name";
const WAKELOOP_HEADER_AVATAR_URL = "x-wakeloop-avatar-url";
const WAKELOOP_HEADER_SPACE_PASSWORD = "x-wakeloop-space-password";
const WAKELOOP_HEADER_ACTIVE_ORGANIZATION_ID = "x-wakeloop-active-organization-id";
const WAKELOOP_HEADER_AGENT_CONTROLLER_REF = "x-wakeloop-agent-controller-ref";
const WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID = "x-wakeloop-agent-controller-conversation-id";
const WAKELOOP_HEADER_DISPATCH_CHAIN_ID = "x-wakeloop-dispatch-chain-id";
const WAKELOOP_HEADER_AUTO_RELAY_DEPTH = "x-wakeloop-auto-relay-depth";
const WAKELOOP_HEADER_VISITED_PROFILE_IDS = "x-wakeloop-visited-profile-ids";
const WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH = "x-wakeloop-visited-profile-ids-hash";
const WAKELOOP_HEADER_FORWARDED_BY = "x-wakeloop-forwarded-by";
const WAKELOOP_EXPECTED_FORWARDED_BY = "wakeloop-gateway";
const MAX_HEADER_LENGTH = 128;
const MAX_ENCODED_HEADER_LENGTH = 1024;
const MAX_AVATAR_URL_HEADER_LENGTH = 2048;
const WAKELOOP_HEADER_TEXT_ENCODING_PREFIX = "utf-8''";
const MAX_PROFILE_ID_LIST_HEADER_LENGTH = MAX_VISITED_PROFILE_IDS * MAX_PROFILE_ID_LENGTH + (MAX_VISITED_PROFILE_IDS - 1);
const MAX_ENCODED_PROFILE_ID_LIST_HEADER_LENGTH = MAX_VISITED_PROFILE_IDS * MAX_PROFILE_ID_LENGTH + (MAX_VISITED_PROFILE_IDS - 1) * 3 + 7;
const PRINTABLE_ASCII_PATTERN = /^[\x20-\x7e]*$/;
const WAKELOOP_MANAGED_REQUEST_CONTEXT_HEADERS = [
	WAKELOOP_HEADER_REQUEST_ID,
	WAKELOOP_HEADER_CALLER_PRINCIPAL_ID,
	WAKELOOP_HEADER_PROFILE_ID,
	WAKELOOP_HEADER_PROFILE_KIND,
	WAKELOOP_HEADER_PROFILE_HANDLE,
	WAKELOOP_HEADER_PROFILE_NAME,
	WAKELOOP_HEADER_OWNER_USER_ID,
	WAKELOOP_HEADER_OWNER_NAME,
	WAKELOOP_HEADER_AVATAR_URL,
	WAKELOOP_HEADER_SPACE_PASSWORD,
	WAKELOOP_HEADER_ACTIVE_ORGANIZATION_ID,
	WAKELOOP_HEADER_AGENT_CONTROLLER_REF,
	WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID,
	WAKELOOP_HEADER_DISPATCH_CHAIN_ID,
	WAKELOOP_HEADER_AUTO_RELAY_DEPTH,
	WAKELOOP_HEADER_VISITED_PROFILE_IDS,
	WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH,
	WAKELOOP_HEADER_FORWARDED_BY
];
/**
* Fetch header values are ByteStrings: undici and workerd throw a TypeError
* for any code point above 0xff, so user-authored text (profile names, owner
* names, Space passwords) cannot ride x-wakeloop-* headers raw. Values that are
* not printable ASCII travel RFC 8187-style as `utf-8''<percent-encoded>`;
* printable-ASCII values pass through unchanged, keeping the wire format of
* machine identifiers identical to older writers.
*/
function encodeWakeLoopHeaderText(value) {
	if (PRINTABLE_ASCII_PATTERN.test(value) && !value.startsWith(WAKELOOP_HEADER_TEXT_ENCODING_PREFIX)) return value;
	return `${WAKELOOP_HEADER_TEXT_ENCODING_PREFIX}${encodeURIComponent(value)}`;
}
function decodeWakeLoopHeaderText(value) {
	if (!value.startsWith(WAKELOOP_HEADER_TEXT_ENCODING_PREFIX)) return value;
	const encoded = value.slice(7);
	try {
		return decodeURIComponent(encoded);
	} catch {
		return value;
	}
}
function readWakeLoopRequestContext(headers, fallbackRequestId) {
	const requestId = safeHeader(headers.get(WAKELOOP_HEADER_REQUEST_ID)) ?? fallbackRequestId;
	const wakeLoopProfileId = safeHeader(headers.get(WAKELOOP_HEADER_PROFILE_ID));
	const profileKind = parseProfileKind(safeHeader(headers.get(WAKELOOP_HEADER_PROFILE_KIND)));
	if (!(wakeLoopProfileId && profileKind)) return null;
	const parsed = wakeLoopRequestContextSchema.safeParse({
		actorContext: {
			requestId,
			...safeHeader(headers.get(WAKELOOP_HEADER_CALLER_PRINCIPAL_ID)) ? { callerPrincipalId: safeHeader(headers.get(WAKELOOP_HEADER_CALLER_PRINCIPAL_ID)) } : {},
			wakeLoopProfileId,
			profileKind,
			...safeHeader(headers.get(WAKELOOP_HEADER_PROFILE_HANDLE)) ? { profileHandle: safeHeader(headers.get(WAKELOOP_HEADER_PROFILE_HANDLE)) } : {},
			...safeHeader(headers.get(WAKELOOP_HEADER_PROFILE_NAME)) ? { profileName: safeHeader(headers.get(WAKELOOP_HEADER_PROFILE_NAME)) } : {},
			...safeHeader(headers.get(WAKELOOP_HEADER_OWNER_USER_ID)) ? { ownerUserId: safeHeader(headers.get(WAKELOOP_HEADER_OWNER_USER_ID)) } : {},
			...safeHeader(headers.get(WAKELOOP_HEADER_OWNER_NAME)) ? { ownerName: safeHeader(headers.get(WAKELOOP_HEADER_OWNER_NAME)) } : {},
			...readAvatarUrlHeader(headers.get(WAKELOOP_HEADER_AVATAR_URL)) ? { avatarUrl: readAvatarUrlHeader(headers.get(WAKELOOP_HEADER_AVATAR_URL)) } : {},
			...safeHeader(headers.get(WAKELOOP_HEADER_AGENT_CONTROLLER_REF)) ? { agentControllerRef: safeHeader(headers.get(WAKELOOP_HEADER_AGENT_CONTROLLER_REF)) } : {},
			...safeHeader(headers.get(WAKELOOP_HEADER_DISPATCH_CHAIN_ID)) ? { dispatchChainId: safeHeader(headers.get(WAKELOOP_HEADER_DISPATCH_CHAIN_ID)) } : {},
			...typeof parseOptionalNonNegativeIntHeader(headers.get(WAKELOOP_HEADER_AUTO_RELAY_DEPTH)) === "number" ? { autoRelayDepth: parseOptionalNonNegativeIntHeader(headers.get(WAKELOOP_HEADER_AUTO_RELAY_DEPTH)) } : {},
			...parseOptionalProfileIdListHeader(headers.get(WAKELOOP_HEADER_VISITED_PROFILE_IDS)).length > 0 ? { visitedProfileIds: parseOptionalProfileIdListHeader(headers.get(WAKELOOP_HEADER_VISITED_PROFILE_IDS)) } : {},
			...safeHeader(headers.get(WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH)) ? { visitedProfileIdsHash: safeHeader(headers.get(WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH)) } : {}
		},
		...safeHeader(headers.get(WAKELOOP_HEADER_ACTIVE_ORGANIZATION_ID)) ? { activeOrganizationId: safeHeader(headers.get(WAKELOOP_HEADER_ACTIVE_ORGANIZATION_ID)) } : {},
		...safeHeader(headers.get(WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID)) ? { agentControllerConversationId: safeHeader(headers.get(WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID)) } : {}
	});
	return parsed.success ? parsed.data : null;
}
function buildWakeLoopRequestContextHeaders(input) {
	const { actorContext } = input.requestContext;
	const headers = {
		[WAKELOOP_HEADER_REQUEST_ID]: actorContext.requestId,
		[WAKELOOP_HEADER_PROFILE_ID]: actorContext.wakeLoopProfileId,
		[WAKELOOP_HEADER_PROFILE_KIND]: actorContext.profileKind
	};
	const callerPrincipalId = normalize(actorContext.callerPrincipalId);
	if (callerPrincipalId) headers[WAKELOOP_HEADER_CALLER_PRINCIPAL_ID] = callerPrincipalId;
	const profileHandle = normalize(actorContext.profileHandle);
	if (profileHandle) headers[WAKELOOP_HEADER_PROFILE_HANDLE] = profileHandle;
	const profileName = normalize(actorContext.profileName);
	if (profileName) headers[WAKELOOP_HEADER_PROFILE_NAME] = profileName;
	const ownerUserId = normalize(actorContext.ownerUserId);
	if (ownerUserId) headers[WAKELOOP_HEADER_OWNER_USER_ID] = ownerUserId;
	const ownerName = normalize(actorContext.ownerName);
	if (ownerName) headers[WAKELOOP_HEADER_OWNER_NAME] = ownerName;
	const avatarUrl = normalize(actorContext.avatarUrl);
	if (avatarUrl) headers[WAKELOOP_HEADER_AVATAR_URL] = avatarUrl;
	const agentControllerRef = normalize(actorContext.agentControllerRef);
	if (agentControllerRef) headers[WAKELOOP_HEADER_AGENT_CONTROLLER_REF] = agentControllerRef;
	const agentControllerConversationId = normalize(input.requestContext.agentControllerConversationId);
	if (agentControllerConversationId) headers[WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID] = agentControllerConversationId;
	const activeOrganizationId = normalize(input.requestContext.activeOrganizationId);
	if (activeOrganizationId) headers[WAKELOOP_HEADER_ACTIVE_ORGANIZATION_ID] = activeOrganizationId;
	const dispatchChainId = normalize(actorContext.dispatchChainId);
	if (dispatchChainId) headers[WAKELOOP_HEADER_DISPATCH_CHAIN_ID] = dispatchChainId;
	if (Number.isInteger(actorContext.autoRelayDepth) && (actorContext.autoRelayDepth ?? -1) >= 0) headers[WAKELOOP_HEADER_AUTO_RELAY_DEPTH] = String(actorContext.autoRelayDepth);
	const visitedProfileIds = normalizeProfileIdList(actorContext.visitedProfileIds);
	if (visitedProfileIds.length > 0) headers[WAKELOOP_HEADER_VISITED_PROFILE_IDS] = visitedProfileIds.join(",");
	const visitedProfileIdsHash = normalize(actorContext.visitedProfileIdsHash);
	if (visitedProfileIdsHash) headers[WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH] = visitedProfileIdsHash;
	if (input.spacePassword !== void 0) {
		const spacePassword = normalizeSpacePasswordText(input.spacePassword);
		if (spacePassword.length > 0) headers[WAKELOOP_HEADER_SPACE_PASSWORD] = spacePassword;
	}
	return encodeHeaderValues(headers);
}
function encodeHeaderValues(headers) {
	const encoded = {};
	for (const [key, value] of Object.entries(headers)) encoded[key] = encodeWakeLoopHeaderText(value);
	return encoded;
}
function applyWakeLoopRequestContextHeaders(target, input, forwardedBy = WAKELOOP_EXPECTED_FORWARDED_BY) {
	for (const key of WAKELOOP_MANAGED_REQUEST_CONTEXT_HEADERS) target.delete(key);
	const headers = buildWakeLoopRequestContextHeaders(input);
	for (const [key, value] of Object.entries(headers)) target.set(key, value);
	target.set(WAKELOOP_HEADER_FORWARDED_BY, forwardedBy);
	return target;
}
function safeHeader(value, { maxEncodedLength = MAX_ENCODED_HEADER_LENGTH, maxLength = MAX_HEADER_LENGTH } = {}) {
	const normalized = normalize(value);
	if (!normalized) return null;
	const decoded = decodeWakeLoopHeaderText(normalized.slice(0, maxEncodedLength)).trim();
	if (!decoded) return null;
	return decoded.slice(0, maxLength);
}
function readAvatarUrlHeader(value) {
	return safeHeader(value, {
		maxEncodedLength: MAX_AVATAR_URL_HEADER_LENGTH,
		maxLength: MAX_AVATAR_URL_HEADER_LENGTH
	});
}
function normalize(value) {
	return String(value ?? "").trim();
}
function parseOptionalNonNegativeIntHeader(value) {
	const normalized = safeHeader(value);
	if (!normalized) return null;
	const parsed = Number.parseInt(normalized, 10);
	if (!Number.isInteger(parsed) || parsed < 0) return null;
	return parsed;
}
function parseOptionalProfileIdListHeader(value) {
	const normalized = safeHeader(value, {
		maxEncodedLength: MAX_ENCODED_PROFILE_ID_LIST_HEADER_LENGTH,
		maxLength: MAX_PROFILE_ID_LIST_HEADER_LENGTH
	});
	if (!normalized) return [];
	return normalizeProfileIdList(normalized.split(","));
}
function normalizeProfileIdList(value) {
	if (!Array.isArray(value)) return [];
	return value.map((item) => normalize(item)).filter((item) => item.length > 0);
}
function parseProfileKind(value) {
	if (value === "human" || value === "agent" || value === "system") return value;
	return null;
}

//#endregion
//#region src/errors.ts
function jsonErrorResponse(input) {
	const payload = gatewayErrorEnvelopeSchema.parse({
		ok: false,
		error: {
			code: input.code,
			message: input.message,
			requestId: input.requestId,
			...input.upstreamCode ? { upstreamCode: input.upstreamCode } : {}
		}
	});
	const response = Response.json(payload, { status: input.status });
	response.headers.set(WAKELOOP_HEADER_REQUEST_ID, input.requestId);
	return response;
}
function wsErrorFrame(input) {
	const payload = gatewayErrorEnvelopeSchema.parse({
		ok: false,
		error: {
			code: input.code,
			message: input.message,
			requestId: input.requestId
		}
	});
	return JSON.stringify({
		type: "system.error",
		error: payload.error
	});
}
function withRequestIdHeader(response, requestId) {
	const headers = new Headers(response.headers);
	headers.set(WAKELOOP_HEADER_REQUEST_ID, requestId);
	return new Response(response.body, {
		status: response.status,
		statusText: response.statusText,
		headers
	});
}
async function readUpstreamErrorMessage(response) {
	const text = (await response.text().catch(() => "")).trim();
	if (!text) return `upstream_http_${response.status}`;
	if (!(response.headers.get("content-type") ?? "").includes("application/json")) return text;
	let parsed;
	try {
		parsed = JSON.parse(text);
	} catch {
		return text;
	}
	if (typeof parsed !== "object" || parsed === null) return text;
	const body = parsed;
	const nestedMessage = typeof body.error === "object" && body.error !== null ? String(body.error.message ?? "").trim() : "";
	if (nestedMessage) return nestedMessage;
	const errorMessage = typeof body.error === "string" ? body.error.trim() : "";
	if (errorMessage) return errorMessage;
	const message = typeof body.message === "string" ? body.message.trim() : "";
	if (message) return message;
	return (typeof body.detail === "string" ? body.detail.trim() : "") || text;
}
const toErrorMessage = toErrorMessage$1;

//#endregion
export { WAKELOOP_EXPECTED_FORWARDED_BY, WAKELOOP_HEADER_ACTIVE_ORGANIZATION_ID, WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID, WAKELOOP_HEADER_AGENT_CONTROLLER_REF, WAKELOOP_HEADER_AUTO_RELAY_DEPTH, WAKELOOP_HEADER_AVATAR_URL, WAKELOOP_HEADER_CALLER_PRINCIPAL_ID, WAKELOOP_HEADER_DISPATCH_CHAIN_ID, WAKELOOP_HEADER_FORWARDED_BY, WAKELOOP_HEADER_OWNER_NAME, WAKELOOP_HEADER_OWNER_USER_ID, WAKELOOP_HEADER_PROFILE_HANDLE, WAKELOOP_HEADER_PROFILE_ID, WAKELOOP_HEADER_PROFILE_KIND, WAKELOOP_HEADER_PROFILE_NAME, WAKELOOP_HEADER_REQUEST_ID, WAKELOOP_HEADER_SPACE_PASSWORD, WAKELOOP_HEADER_VISITED_PROFILE_IDS, WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH, applyWakeLoopRequestContextHeaders, buildWakeLoopRequestContextHeaders, contextAuditFields, decodeWakeLoopHeaderText, encodeWakeLoopHeaderText, jsonErrorResponse, readUpstreamErrorMessage, readWakeLoopRequestContext, toErrorMessage, withRequestIdHeader, wsErrorFrame };
//# sourceMappingURL=index.js.map